import type { PostProcessorModule } from 'i18next'

/**
 * "Open in place" for the translation review dashboard (/dashboard/translations): a page opened
 * with `?i18nHighlight=<ns:key>` outlines every element showing that string, so a reviewer sees
 * where it sits. With it, `?lang=<code>` opens the page in that language for the visit (it is not
 * saved, and the profile language does not take over).
 *
 * Nothing here runs without the parameter: the post-processor is only registered, and the
 * observer only started, when the page was opened with it.
 */

export const HIGHLIGHT_PARAM = 'i18nHighlight'
export const HIGHLIGHT_CLASS = 'i18n-review-highlight'
/** Wraps a highlighted value: an invisible separator, so the text reads the same. */
export const HIGHLIGHT_MARK = '⁣'

const readParam = (name: string): string | null => {
  try {
    return new URLSearchParams(window.location.search).get(name)
  } catch {
    return null
  }
}

/** The `ns:key` to outline on this page, or null. */
export const highlightTarget = (): string | null => {
  const value = readParam(HIGHLIGHT_PARAM)
  return value && /^[A-Za-z]+:[\w.-]+$/.test(value) ? value : null
}

/** Opened from the review dashboard: `?lang=` applies for this visit only. */
export const reviewLanguageOverride = (): boolean => !!highlightTarget() && !!readParam('lang')

/** The full `ns:key` of a t() call: the key may carry its namespace, else it comes from the options. */
export const fullKey = (key: string, options: any, defaultNS = 'common'): string => {
  if (key.includes(':')) return key
  const ns = Array.isArray(options?.ns) ? options.ns[0] : options?.ns
  return `${ns || defaultNS}:${key}`
}

export const highlightPostProcessor = (target: string | null = highlightTarget()): PostProcessorModule => ({
  type: 'postProcessor',
  name: 'i18nHighlight',
  process(value: string, key: string | string[], options: any, translator: any) {
    if (!target || typeof value !== 'string') return value
    const keys = Array.isArray(key) ? key : [key]
    const defaultNS = translator?.options?.defaultNS
    const hit = keys.some((k) => typeof k === 'string' && fullKey(k, options, Array.isArray(defaultNS) ? defaultNS[0] : defaultNS) === target)
    return hit ? `${HIGHLIGHT_MARK}${value}${HIGHLIGHT_MARK}` : value
  },
})

/** Outline the elements whose text carries the mark, now and as the page renders. */
export const outlineMarked = (root: ParentNode = document.body): number => {
  let found = 0
  const walker = document.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    if (node.nodeValue?.includes(HIGHLIGHT_MARK) && node.parentElement) {
      if (!node.parentElement.classList.contains(HIGHLIGHT_CLASS)) {
        node.parentElement.classList.add(HIGHLIGHT_CLASS)
        if (!found) node.parentElement.scrollIntoView?.({ block: 'center' })
      }
      found++
    }
    node = walker.nextNode()
  }
  return found
}

let observer: MutationObserver | null = null

export const startHighlighting = () => {
  if (observer || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
  const style = document.createElement('style')
  style.textContent = `.${HIGHLIGHT_CLASS} { outline: 2px solid #FC9F5B !important; outline-offset: 2px; }`
  document.head.appendChild(style)
  observer = new MutationObserver(() => outlineMarked())
  observer.observe(document.body, { childList: true, subtree: true, characterData: true })
  outlineMarked()
}
